/**
 * Détection de connexion lente — feedback utilisateur.
 *
 * But : quand le réseau est lent, le dire, pour que l'utilisateur comprenne
 * que l'attente vient de la connexion (et non d'un bug). Deux signaux :
 *  - une requête reste sans réponse plus de 4 s (mesure réelle, tout réseau) ;
 *  - le navigateur annonce un réseau 2G (`navigator.connection`, Chrome et
 *    Android seulement — ailleurs, seul le premier signal compte).
 * L'état est affiché par le bandeau de connexion (`ConnectionBanner`), au même
 * endroit que « hors ligne » et « serveur indisponible ».
 *
 * Volontairement SANS timeout côté axios : certains appels légitimes (exports
 * PDF/Excel) peuvent durer plusieurs minutes. On informe, on n'interrompt pas.
 */
import { useSyncExternalStore } from "react";

// Au-delà de ce délai sans réponse, une requête est considérée « lente ».
const SLOW_AFTER_MS = 4000;

/** Types de réseau annoncés comme trop lents pour un usage confortable. */
const WEAK_EFFECTIVE_TYPES = new Set(["slow-2g", "2g"]);

// Nombre de requêtes actuellement « lentes » : l'état reste tant qu'il en reste
// au moins une en cours.
let slowCount = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

interface NetworkInformationLike {
  effectiveType?: string;
  addEventListener?: (type: "change", cb: () => void) => void;
  removeEventListener?: (type: "change", cb: () => void) => void;
}

function networkInformation(): NetworkInformationLike | null {
  if (typeof navigator === "undefined") return null;
  const conn: unknown = (navigator as Navigator & { connection?: unknown }).connection;
  return typeof conn === "object" && conn !== null ? (conn as NetworkInformationLike) : null;
}

/** Le navigateur annonce-t-il un réseau 2G ? */
export function isWeakConnection(): boolean {
  const type = networkInformation()?.effectiveType;
  return typeof type === "string" && WEAK_EFFECTIVE_TYPES.has(type);
}

/** `true` quand au moins une requête traîne, ou que le réseau annoncé est 2G. */
export function isSlowNetwork(): boolean {
  return slowCount > 0 || isWeakConnection();
}

export function subscribeSlowNetwork(listener: () => void): () => void {
  listeners.add(listener);
  const conn = networkInformation();
  conn?.addEventListener?.("change", listener);
  return () => {
    listeners.delete(listener);
    conn?.removeEventListener?.("change", listener);
  };
}

export function useIsSlowNetwork(): boolean {
  return useSyncExternalStore(subscribeSlowNetwork, isSlowNetwork, () => false);
}

/**
 * À appeler au départ d'une requête. Renvoie la fonction à appeler à sa fin
 * (succès OU erreur), qui nettoie le minuteur et lève l'état « lent » si plus
 * aucune requête ne traîne.
 */
export function trackRequest(): () => void {
  let fired = false;
  const timer = setTimeout(() => {
    fired = true;
    slowCount += 1;
    if (slowCount === 1) emit();
  }, SLOW_AFTER_MS);

  let ended = false;
  return () => {
    if (ended) return; // idempotent : un retry peut appeler la fin deux fois
    ended = true;
    clearTimeout(timer);
    if (fired) {
      slowCount = Math.max(0, slowCount - 1);
      if (slowCount === 0) emit();
    }
  };
}

/** Remise à zéro — réservée aux tests. */
export function resetSlowNetwork(): void {
  slowCount = 0;
  listeners.clear();
}
